import React, { useState } from 'react';
import { ExternalLink, Folder, Github, Star } from 'lucide-react';
import { projects } from '../data/mock';
import { Button } from './ui/button';

const ProjectsSection = () => {
  const categories = ['All', ...new Set(projects.map((p) => p.category))];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProjects =
    activeCategory === 'All'
      ? projects
      : projects.filter((p) => p.category === activeCategory);

  const getCategoryColor = (category) => {
    const colors = {
      'IAM & Security': 'bg-red-500/10 border-red-500/30 text-red-400',
      'DevOps': 'bg-purple-500/10 border-purple-500/30 text-purple-400',
      'AI': 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
      'Backend': 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
      'Blockchain': 'bg-orange-500/10 border-orange-500/30 text-orange-400',
      'Infrastructure': 'bg-amber-500/10 border-amber-500/30 text-amber-400'
    };
    return colors[category] || 'bg-zinc-500/10 border-zinc-500/30 text-zinc-400';
  };

  const getSourceBadge = (source) => {
    const badges = {
      'Personal': 'bg-zinc-800 text-zinc-400',
      'Nevcodia IX': 'bg-blue-900/50 text-blue-400',
      'Shyntr': 'bg-purple-900/50 text-purple-400'
    };
    return badges[source] || 'bg-zinc-800 text-zinc-400';
  };

  return (
    <section id="projects" className="py-24 bg-zinc-900/30">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="mb-12">
          <p className="text-cyan-400 font-mono text-sm mb-2">// PROJECTS</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white">Featured Work</h2>
          <p className="text-zinc-500 mt-4">
            Open source projects from{' '}
            <a href="https://github.com/nevzatcirak" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">@nevzatcirak</a>
            {', '}
            <a href="https://github.com/nevcodia" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">Nevcodia IX</a>
            {' & '}
            <a href="https://github.com/Shyntr" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">Shyntr</a>
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeCategory === category
                  ? 'bg-cyan-500 text-zinc-950'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-700'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <a
              key={project.id}
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group p-6 bg-zinc-950 border border-zinc-800 rounded-2xl hover:border-cyan-500/50 transition-all duration-300 block flex flex-col"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 bg-cyan-500/10 border border-cyan-500/20 rounded-lg flex items-center justify-center">
                  {project.category === 'AI' ? (
                    <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ) : (
                    <Folder className="w-5 h-5 text-cyan-400" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {project.stars && (
                    <span className="flex items-center gap-1 text-xs text-zinc-500">
                      <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                      {project.stars}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-zinc-500 hover:text-cyan-400 h-8 w-8"
                  >
                    {project.link.includes('github') ? (
                      <Github className="w-4 h-4" />
                    ) : (
                      <ExternalLink className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Badges */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={`px-2 py-0.5 border rounded-full text-xs ${getCategoryColor(project.category)}`}>
                  {project.category}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${getSourceBadge(project.source)}`}>
                  {project.source}
                </span>
              </div>

              {/* Title & Description */}
              <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                {project.title}
              </h3>
              <p className="text-zinc-500 text-sm mb-4 line-clamp-2 flex-grow">
                {project.description}
              </p>

              {/* Technologies */}
              <div className="flex flex-wrap gap-1.5 mt-auto">
                {project.technologies.slice(0, 4).map((tech) => (
                  <span
                    key={tech}
                    className="px-2 py-0.5 bg-zinc-900/50 border border-zinc-800 rounded text-xs text-zinc-400 font-mono"
                  >
                    {tech}
                  </span>
                ))}
                {project.technologies.length > 4 && (
                  <span className="px-2 py-0.5 text-xs text-zinc-500">
                    +{project.technologies.length - 4}
                  </span>
                )}
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
